import { useMemo } from 'react';
import { useIbanFormatter } from './iban-formatter';
import type { TokenCampaignResponse } from '@/types/campaign';

const IBAN_SESSION_STORAGE_KEY = 'iban';
const FULL_NAME_SESSION_STORAGE_KEY = 'full-name';
const CAMPAIGN_SESSION_STORAGE_KEY = 'campaign';

export function useSessionStorage() {
  const { sanitizeIban, formatIban } = useIbanFormatter();

  const storedIban = useMemo(
    () => window.sessionStorage.getItem(IBAN_SESSION_STORAGE_KEY) || '',
    []
  );

  const storedFullName = useMemo(
    () => window.sessionStorage.getItem(FULL_NAME_SESSION_STORAGE_KEY) || '',
    []
  );

  const updateIban = (iban: string) => {
    const normalizedIban = sanitizeIban(iban);
    const formattedIban = formatIban(normalizedIban);

    window.sessionStorage.setItem(IBAN_SESSION_STORAGE_KEY, formattedIban);
  };

  const updateFullName = (fullName: string) => {
    window.sessionStorage.setItem(FULL_NAME_SESSION_STORAGE_KEY, fullName);
  };

  const storeCampaign = (campaign: TokenCampaignResponse) => {
    try {
      const serialized = JSON.stringify(campaign);
      if (serialized === undefined) {
        throw new Error('Campaign could not be serialized');
      }

      window.sessionStorage.setItem(CAMPAIGN_SESSION_STORAGE_KEY, serialized);
    } catch (error) {
      console.error('Failed to store campaign', error);
    }
  };

  const getCampaign = (
    campaignId: string
  ): TokenCampaignResponse | undefined => {
    let serialized: string | null;

    try {
      serialized = window.sessionStorage.getItem(CAMPAIGN_SESSION_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to read campaign', error);
      return undefined;
    }

    if (serialized === null) {
      return undefined;
    }

    let campaign: TokenCampaignResponse;
    try {
      campaign = JSON.parse(serialized) as TokenCampaignResponse;
    } catch (error) {
      console.error('Failed to parse stored campaign', error);
      clearCampaign();
      return undefined;
    }

    if (campaign.id !== campaignId) {
      return undefined;
    }

    return campaign;
  };

  const clearCampaign = () => {
    try {
      window.sessionStorage.removeItem(CAMPAIGN_SESSION_STORAGE_KEY);
    } catch (error) {
      console.error('Failed to clear campaign', error);
    }
  };

  return {
    storedIban,
    storedFullName,
    updateIban,
    updateFullName,
    storeCampaign,
    getCampaign,
    clearCampaign,
  };
}
